// src/components/ApprovalWorkflowManager.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function ApprovalWorkflowManager({ formId, companyId }) {
  const [workflow, setWorkflow] = useState({ enabled: false, stages: [] });
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);

  // Load workflow configuration and users/groups
  useEffect(() => {
    if (!formId || !companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    Promise.all([
      loadWorkflow(),
      loadUsers(),
      loadGroups()
    ]).finally(() => setLoading(false));
  }, [formId, companyId]);

  async function loadWorkflow() {
    try {
      const workflowRef = doc(db, 'companies', companyId, 'forms', formId, 'settings', 'approvalWorkflow');
      const workflowSnap = await getDoc(workflowRef);
      
      if (workflowSnap.exists()) {
        setWorkflow(workflowSnap.data());
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
    }
  }

  async function loadUsers() {
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const usersData = usersSnap.docs
        .filter(doc => doc.data().companyId === companyId)
        .map(doc => ({
          id: doc.id,
          email: doc.data().email,
          name: doc.data().name || doc.data().email,
          role: doc.data().role || 'user'
        }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }

  async function loadGroups() {
    try {
      // Groups are stored as an array in the company document
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        const groupsArray = companyData.groups || [];
        // Convert group names to objects with id and name
        const groupsData = groupsArray.map((groupName) => ({
          id: groupName, // Use the group name as ID
          name: groupName
        }));
        setGroups(groupsData);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }

  async function saveWorkflow() {
    if (!formId || !companyId) return;

    setSaving(true);
    try {
      const workflowRef = doc(db, 'companies', companyId, 'forms', formId, 'settings', 'approvalWorkflow');
      await setDoc(workflowRef, {
        ...workflow,
        updatedAt: new Date()
      });
      alert('Approval workflow saved successfully!');
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('Failed to save workflow: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  function addStage() {
    const newStage = {
      stageNumber: workflow.stages.length + 1,
      stageName: `Stage ${workflow.stages.length + 1}`,
      approvers: { users: [], groups: [], roles: [] },
      requirements: { signature: false, comment: false, decision: true },
      requireAll: false
    };
    setEditingStage(newStage);
    setShowStageModal(true);
  }

  function editStage(stage) {
    setEditingStage({ ...stage });
    setShowStageModal(true);
  }

  function deleteStage(stageNumber) {
    if (!window.confirm('Delete this approval stage?')) return;
    
    const updatedStages = workflow.stages
      .filter(s => s.stageNumber !== stageNumber)
      .map((s, index) => ({ ...s, stageNumber: index + 1 }));
    
    setWorkflow({ ...workflow, stages: updatedStages });
  }

  function saveStage() {
    if (!editingStage.stageName.trim()) {
      alert('Stage name is required');
      return;
    }

    const hasApprovers = 
      editingStage.approvers.users.length > 0 ||
      editingStage.approvers.groups.length > 0 ||
      editingStage.approvers.roles.length > 0;

    if (!hasApprovers) {
      alert('At least one approver (user, group, or role) is required');
      return;
    }

    const existingIndex = workflow.stages.findIndex(s => s.stageNumber === editingStage.stageNumber);
    
    if (existingIndex >= 0) {
      const updatedStages = [...workflow.stages];
      updatedStages[existingIndex] = editingStage;
      setWorkflow({ ...workflow, stages: updatedStages });
    } else {
      setWorkflow({ ...workflow, stages: [...workflow.stages, editingStage] });
    }

    setShowStageModal(false);
    setEditingStage(null);
  }

  function toggleUser(userId) {
    const users = editingStage.approvers.users || [];
    const updated = users.includes(userId)
      ? users.filter(id => id !== userId)
      : [...users, userId];
    
    setEditingStage({
      ...editingStage,
      approvers: { ...editingStage.approvers, users: updated }
    });
  }

  function toggleGroup(groupId) {
    const groups = editingStage.approvers.groups || [];
    const updated = groups.includes(groupId)
      ? groups.filter(id => id !== groupId)
      : [...groups, groupId];
    
    setEditingStage({
      ...editingStage,
      approvers: { ...editingStage.approvers, groups: updated }
    });
  }

  function addRole() {
    const roleName = window.prompt('Enter role name (e.g., "Health & Safety Lead"):');
    if (!roleName || !roleName.trim()) return;

    const roles = editingStage.approvers.roles || [];
    if (roles.includes(roleName.trim())) {
      alert('Role already added');
      return;
    }

    setEditingStage({
      ...editingStage,
      approvers: { ...editingStage.approvers, roles: [...roles, roleName.trim()] }
    });
  }

  function removeRole(roleName) {
    const roles = editingStage.approvers.roles || [];
    setEditingStage({
      ...editingStage,
      approvers: { ...editingStage.approvers, roles: roles.filter(r => r !== roleName) }
    });
  }

  if (!formId || !companyId) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500 dark:text-gray-400 mb-4">
          💾 Save your form first to configure approval workflow
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin text-4xl mb-4">⚙️</div>
        <div className="text-gray-600 dark:text-gray-300">Loading approval workflow...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          Approval Workflow
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure multi-stage approval requirements for this check
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/40">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={workflow.enabled}
            onChange={(e) => setWorkflow({ ...workflow, enabled: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <div className="font-semibold text-gray-800 dark:text-gray-100">
              Require Approval for This Check
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              When enabled, submitted checks will require approval before being marked as complete
            </div>
          </div>
        </label>
      </div>

      {workflow.enabled && (
        <>
          {/* Stages List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Approval Stages ({workflow.stages.length})
              </h3>
              <button
                onClick={addStage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                + Add Stage
              </button>
            </div>

            {workflow.stages.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                <div className="text-4xl mb-2">📋</div>
                <div className="text-gray-600 dark:text-gray-400">
                  No approval stages configured yet
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Add your first approval stage to get started
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {workflow.stages
                  .sort((a, b) => a.stageNumber - b.stageNumber)
                  .map((stage) => (
                    <div
                      key={stage.stageNumber}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-bold rounded">
                              Stage {stage.stageNumber}
                            </span>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                              {stage.stageName}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {stage.requireAll ? 'All approvers must approve' : 'Any one approver can approve'}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editStage(stage)}
                            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => deleteStage(stage.stageNumber)}
                            className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                            Approvers
                          </div>
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            {stage.approvers.users.length > 0 && (
                              <div>👤 {stage.approvers.users.length} user(s)</div>
                            )}
                            {stage.approvers.groups.length > 0 && (
                              <div>👥 {stage.approvers.groups.length} group(s)</div>
                            )}
                            {stage.approvers.roles.length > 0 && (
                              <div>🎭 {stage.approvers.roles.length} role(s)</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                            Requirements
                          </div>
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            {stage.requirements.signature && <div>✍️ Signature</div>}
                            {stage.requirements.comment && <div>💬 Comment</div>}
                            {stage.requirements.decision && <div>✅ Decision</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={saveWorkflow}
              disabled={saving}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : '💾 Save Workflow'}
            </button>
          </div>
        </>
      )}

      {/* Stage Edit Modal */}
      {showStageModal && editingStage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                {editingStage.stageNumber > workflow.stages.length ? 'Add' : 'Edit'} Approval Stage
              </h3>

              {/* Stage Name */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Stage Name *
                </label>
                <input
                  type="text"
                  value={editingStage.stageName}
                  onChange={(e) => setEditingStage({ ...editingStage, stageName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  placeholder="e.g., Supervisor Review"
                />
              </div>

              {/* Approvers */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Assign Approvers *
                </label>

                {/* Users */}
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    👤 Individual Users
                  </div>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/30">
                    {users.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-500 p-2">
                        No users available
                      </div>
                    ) : (
                      users.map(user => (
                        <label key={user.id} className="flex items-center gap-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingStage.approvers.users.includes(user.id)}
                            onChange={() => toggleUser(user.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {user.name} ({user.role})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Groups */}
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    👥 Groups
                  </div>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/30">
                    {groups.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-500 p-2">
                        No groups available
                      </div>
                    ) : (
                      groups.map(group => (
                        <label key={group.id} className="flex items-center gap-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingStage.approvers.groups.includes(group.id)}
                            onChange={() => toggleGroup(group.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {group.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Roles */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      🎭 Roles
                    </div>
                    <button
                      onClick={addRole}
                      className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                    >
                      + Add Role
                    </button>
                  </div>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/30 min-h-[60px]">
                    {!editingStage.approvers.roles || editingStage.approvers.roles.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-500 p-2">
                        No roles assigned
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {editingStage.approvers.roles.map(role => (
                          <div
                            key={role}
                            className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm rounded-full flex items-center gap-1"
                          >
                            {role}
                            <button
                              onClick={() => removeRole(role)}
                              className="ml-1 hover:text-purple-900 dark:hover:text-purple-100"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Requirements
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingStage.requirements.signature}
                      onChange={(e) => setEditingStage({
                        ...editingStage,
                        requirements: { ...editingStage.requirements, signature: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      ✍️ Require Signature
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingStage.requirements.comment}
                      onChange={(e) => setEditingStage({
                        ...editingStage,
                        requirements: { ...editingStage.requirements, comment: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      💬 Require Comment
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingStage.requirements.decision}
                      onChange={(e) => setEditingStage({
                        ...editingStage,
                        requirements: { ...editingStage.requirements, decision: e.target.checked }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      ✅ Require Decision (Approve/Reject)
                    </span>
                  </label>
                </div>
              </div>

              {/* Require All */}
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStage.requireAll}
                    onChange={(e) => setEditingStage({ ...editingStage, requireAll: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Require All Approvers
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      If checked, all assigned approvers must approve. Otherwise, any one approval is sufficient.
                    </div>
                  </div>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowStageModal(false);
                    setEditingStage(null);
                  }}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveStage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Save Stage
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
